import React, {useMemo} from "react"
import PropTypes from "prop-types"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import {Box, Paper, Stack, Tooltip, Typography} from "@mui/material"
import ButtonWithIcon from "../Button/Button"
import DataGridTable from "../Table/data-grid-table"
import StatusBadge from "../StatusBadge"
import WorkflowProgress from "./workflow-progress"
import {
    formatWorkflowDateTime,
    workflowStatusPresentation,
} from "./workflow-execution"

const WorkflowExecutionsTable = ({
    rows,
    total,
    page,
    pageSize,
    loading,
    resetKey,
    onPageChange,
    onOpenExecution,
    onRefresh,
}) => {
    const columns = useMemo(() => [
        {
            field: "contract_version",
            headerName: "Contrato",
            minWidth: 90,
            renderCell: ({value}) => (
                <StatusBadge
                    label={(value || "v1").toUpperCase()}
                    backgroundColor={value === "v2" ? "#5C6BC0" : "#667085"}
                    radius={1}
                />
            ),
        },
        {field: "dag_id", headerName: "Rotina", minWidth: 270, flex: 1},
        {field: "reference_date", headerName: "Referência", minWidth: 120},
        {
            field: "status",
            headerName: "Status",
            minWidth: 170,
            renderCell: ({value, row}) => {
                const status = workflowStatusPresentation(value)
                return (
                    <Tooltip title={`Processados ${row.progress_processed} de ${row.progress_total}`}>
                        <span><StatusBadge label={status.text} backgroundColor={status.color} radius={1}/></span>
                    </Tooltip>
                )
            },
        },
        {
            field: "current_stage",
            headerName: "Etapa atual",
            minWidth: 200,
            valueGetter: ({value}) => value || "—",
        },
        {
            field: "progress_percent",
            headerName: "Progresso",
            minWidth: 180,
            sortable: false,
            renderCell: ({row}) => (
                <WorkflowProgress
                    compact
                    processed={row.progress_processed}
                    total={row.progress_total}
                    percent={row.progress_percent}
                />
            ),
        },
        {
            field: "created_by_name",
            headerName: "Usuário",
            minWidth: 150,
            valueGetter: ({row}) => row.created_by_name || row.created_by || "—",
        },
        {
            field: "heartbeat_at",
            headerName: "Heartbeat",
            minWidth: 175,
            valueGetter: ({value}) => formatWorkflowDateTime(value),
        },
        {
            field: "created_at",
            headerName: "Criação",
            minWidth: 175,
            valueGetter: ({value}) => formatWorkflowDateTime(value),
        },
        {
            field: "current_error",
            headerName: "Erro atual",
            minWidth: 180,
            sortable: false,
            renderCell: ({row}) => row.current_error ? (
                <Tooltip title={row.current_error.message || row.current_error.code}>
                    <span><StatusBadge label={row.current_error.code || "Erro"} backgroundColor="error" radius={1}/></span>
                </Tooltip>
            ) : "—",
        },
    ], [])

    return (
        <Paper sx={{p: 3, backgroundColor: "background.paper"}}>
            <Stack
                direction={{xs: "column", sm: "row"}}
                spacing={2}
                alignItems={{xs: "flex-start", sm: "center"}}
                justifyContent="space-between"
                sx={{mb: 2}}
            >
                <Typography variant="h3" color="common.white">Últimas execuções</Typography>
                <ButtonWithIcon icon={<RefreshRoundedIcon/>} onClick={onRefresh} disabled={loading}>
                    ATUALIZAR
                </ButtonWithIcon>
            </Stack>
            <Box sx={{width: "100%", overflowX: "auto"}}>
                <DataGridTable
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    serverSide
                    rowCount={total}
                    page={page}
                    pageSize={pageSize}
                    resetKey={resetKey}
                    onServerPageChange={onPageChange}
                    onRowClick={onOpenExecution ? ({row}) => onOpenExecution(row) : undefined}
                    rowHeight={64}
                    pointerCursor={Boolean(onOpenExecution)}
                    disableRowSelectionOnClick
                    disableColumnFilter
                    sx={{minWidth: 1180}}
                />
            </Box>
        </Paper>
    )
}

WorkflowExecutionsTable.propTypes = {
    rows: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    loading: PropTypes.bool,
    resetKey: PropTypes.string,
    onPageChange: PropTypes.func.isRequired,
    onOpenExecution: PropTypes.func,
    onRefresh: PropTypes.func.isRequired,
}

export default WorkflowExecutionsTable
